import { useEffect, useRef, useState } from 'react';
import { getImageUrl } from '@/utils/images';
import { useHlsPlayback } from './use-hls-playback';

/**
 * Streaming-video player block (`resurs/streaming-video`).
 *
 * Plays an HLS master playlist produced by the Image Automator 3000 video
 * pipeline. Replaced the direct-playback `resurs/uploaded-video` block,
 * which was removed once its published usages were migrated here. Two
 * playback paths:
 *
 *   1. **Native HLS** — true Safari (desktop + iOS) decodes
 *      `application/vnd.apple.mpegurl` natively. The `<source>` tag inside
 *      the `<video>` is enough; no JavaScript needed. SSR-friendly.
 *   2. **hls.js** — every other browser dynamically imports hls.js and
 *      attaches a MediaSource via the shared `useHlsPlayback` hook (which
 *      also handles ABR overrides, autoplay-after-manifest, and error
 *      recovery).
 *
 * Honours `prefers-reduced-motion` the same way `video-section.tsx` does:
 * when the user asks for reduced motion we never autoplay, and we pause the
 * video if the OS setting is switched on mid-playback (WCAG 2.3.3).
 *
 * Renders nothing if no `masterUrl` is provided. The WP `block.php` returns
 * `null` from the headless filter when the IA3K service can't build a
 * master URL — the editor's `/video-status` save gate prevents this in
 * practice for new pages.
 */
export type StreamingVideoProps = {
    masterUrl?: string;
    posterImage?: {
        src: string;
        __isimage?: boolean;
    } | null;
    autoplay?: boolean;
    loop?: boolean;
};

export const StreamingVideo = ({ masterUrl, posterImage, autoplay = false, loop = false }: StreamingVideoProps) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);

    // Starts false so SSR markup and the first client render agree; the effect
    // corrects it after mount.
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        setPrefersReducedMotion(mq.matches);
        const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    const shouldAutoplay = autoplay && !prefersReducedMotion;

    useHlsPlayback(videoRef, { masterUrl, autoplay: shouldAutoplay });

    // Safari's native path can already be playing from the SSR'd autoplay
    // attribute before the effect above runs, and dropping the attribute does
    // not stop it — pause explicitly. No auto-resume if toggled back off.
    useEffect(() => {
        const el = videoRef.current;
        if (!el) return;
        if (prefersReducedMotion && !el.paused) {
            el.pause();
        }
    }, [prefersReducedMotion]);

    if (!masterUrl) {
        return null;
    }

    // Poster URL resolution. Mirrors `video-section.tsx`:
    //   - IA3K auto-poster URLs (`/automator/...`) are served by the
    //     automator's image proxy and pass through unchanged.
    //   - Manual posters arrive as a relative `/uploads/...` path from
    //     `block.php` (resolved via `rawb_get_image_as_object()`); they
    //     need to go through `getImageUrl()` so the rawb-images service
    //     rewrites them to `/image/{size}/...`. Without this the browser
    //     fetches a URL the public frontend can't resolve.
    const posterUrl = posterImage?.src
        ? posterImage.src.startsWith('/automator/')
            ? posterImage.src
            : getImageUrl(posterImage.src, '1920xAUTO')
        : undefined;

    return (
        <video
            ref={videoRef}
            className="r-w-full"
            controls
            playsInline
            preload="metadata"
            // crossOrigin matches the upstream IA3K player; required so the
            // browser sends an Origin header and respects CORS on segment
            // fetches when fronted by a CDN.
            crossOrigin="anonymous"
            // Autoplay implies muted by browser policy. Stays keyed to the
            // requested `autoplay`, not `shouldAutoplay`: when reduced motion
            // cancels autoplay the video simply starts paused and muted, and
            // the visible controls let the viewer unmute.
            muted={autoplay}
            autoPlay={shouldAutoplay}
            loop={loop}
            poster={posterUrl}
        >
            {/*
                Provide the master URL as a <source> with the HLS MIME type.
                Safari + iOS pick this up natively and start playback without
                any JavaScript. Other browsers ignore it (can't decode it)
                and rely on the hls.js attach inside useHlsPlayback.
            */}
            <source src={masterUrl} type="application/vnd.apple.mpegurl" />
        </video>
    );
};
